import { Category, EditorListing } from 'src/common/types/api/listings';
import { mapListingToInitialData } from '../components/PricingPanel/pricingPanelHelper';
import {
  CANCELATION_ANTICIPATION_FIELD,
  CANCELATION_REFUND_FIELD,
  MAXIMUM_HOURS_FIELD,
  MAXIMUM_MULTIDAY_FIELD,
} from '../constants';
import {
  validateDescription,
  validateHeadline,
  validatePrimaryCategory,
  validateShortname,
} from './listingProgressValidation/validateEssentialsPanel';
import { validatePhotosCount } from './listingProgressValidation/validatePhotosPanel';
import { validateVesselDetails } from './listingProgressValidation/validateVesselDetailsPanel';
import { verboseValidateAllPriceTiers } from './listingProgressValidation/validatePricingPanel';
import { validateAddress } from './listingProgressValidation/validateLocationPanel';

/**
 * Runs validation on fields related to the Essentials tab that are available when
 * querying for all listings.
 * The field values that are returned from API when querying for full listings can sometimes
 * be quite different and have much less information to use for validation compared to
 * querying an individual listing. Summary vs the full details. This only affects some fields.
 */
type EssentialsFieldsComplete = Pick<EditorListing,
| 'headline'
| 'shortname'
| 'description'
| 'capacity'
| 'categories'
| 'category_count'>;
export const essentialsFieldsComplete = ({
  headline,
  shortname,
  description,
  categories,
  category_count: categoryCount,
}: EssentialsFieldsComplete): boolean => (
  validateHeadline(headline)
    && validateShortname(shortname)
    && validateDescription(description)
    && validatePrimaryCategory({
      categories: categories as Category[],
      categoryCount,
    })
);

/**
 * Runs validation on fields related to the Location tab that are available when
 * querying for all listings.
 * The field values that are returned from API when querying for full listings can sometimes
 * be quite different and have much less information to use for validation compared to
 * querying an individual listing. Summary vs the full details. This only affects some fields.
 */
type LocationFieldsComplete = Pick<EditorListing, 'address' | 'latitude' | 'longitude' >;
export const locationFieldsComplete = ({
  address,
  latitude,
  longitude,
}: LocationFieldsComplete): boolean => (
  validateAddress({
    address,
    latitude,
    longitude,
  })
);

/**
 * Runs validation on fields related to the Pricing tab that are available when
 * querying for all listings.
 * The field values that are returned from API when querying for full listings can sometimes
 * be quite different and have much less information to use for validation compared to
 * querying an individual listing. Summary vs the full details. This only affects some fields.
 */
export const pricingFieldsComplete = (
  listing: Partial<EditorListing>,
  isImpersonatedSession: boolean = false,
): boolean => {
  const tiers = mapListingToInitialData(listing as EditorListing, isImpersonatedSession);

  const validation = verboseValidateAllPriceTiers({
    hour: tiers.hour,
    multiday: tiers.multiday,
    durationLimit: {
      min: 1,
      hourMax: listing[MAXIMUM_HOURS_FIELD],
      multidayMax: listing[MAXIMUM_MULTIDAY_FIELD],
    },
    cancellation: {
      refund: listing[CANCELATION_REFUND_FIELD],
      anticipation: listing[CANCELATION_ANTICIPATION_FIELD],
    },
  });

  return validation.valid;
};

/**
 * Runs validation on fields related to the Photos tab that are available when
 * querying for all listings.
 * The field values that are returned from API when querying for full listings can sometimes
 * be quite different and have much less information to use for validation compared to
 * querying an individual listing. Summary vs the full details. This only affects some fields.
 */
type PhotosFieldsComplete = Pick<EditorListing, 'photo_count'>;
export const photosFieldsComplete = ({
  photo_count: photoCount,
}: PhotosFieldsComplete): boolean => validatePhotosCount(photoCount);

/**
 * Runs validation on fields related to the Vessel Details tab that are available when
 * querying for all listings.
 * The field values that are returned from API when querying for full listings can sometimes
 * be quite different and have much less information to use for validation compared to
 * querying an individual listing. Summary vs the full details. This only affects some fields.
 */
type VesselDetailsFieldsComplete = Pick<EditorListing, 'year' | 'make' | 'model' | 'max_length' | 'capacity'>;
export const vesselDetailsFieldsComplete = ({
  year, make, model, max_length, capacity,
}: VesselDetailsFieldsComplete): boolean => validateVesselDetails({
  year, make, model, max_length, capacity,
});
